// The ALL stance's birth question and its carriage. Standard Claude Code:
// entitlement is the whole computer, a project is a folder, and the folder is
// the boundary that scopes everything nested inside it. Here entitlement is
// every World the login's membership rows grant, and a World is that folder:
// the boundary that scopes the work and, through its workspace binding, where
// the code and files live. A new session born from ALL must make that
// association exactly once — so the surfaces ask WHICH World, then the chosen
// World's own governed ceremony runs on arrival.

const PENDING_BIRTH_STORAGE_KEY = "neucleos.pendingGovernedBirth";
/** A pending birth is a gesture in flight, not a standing order. */
const PENDING_BIRTH_TTL_MS = 2 * 60 * 1000;

// -- The ask: a tiny window-event bus (TR2/TR3 retired the room buses into
// -- worldViewController; this chooser ask is a named absence of that leg) ----

const OPEN_WORLD_BIRTH_CHOOSER_EVENT = "cleo:open-world-birth-chooser";

let seatedChoosers = 0;

/**
 * Ask the seated chooser to open. Returns false when no chooser is seated so
 * the caller keeps its teaching-toast fallback.
 */
export function openWorldBirthChooser(): boolean {
  if (seatedChoosers === 0) return false;
  window.dispatchEvent(new CustomEvent(OPEN_WORLD_BIRTH_CHOOSER_EVENT));
  return true;
}

export function onOpenWorldBirthChooser(listener: () => void): () => void {
  const handler = () => listener();
  seatedChoosers += 1;
  window.addEventListener(OPEN_WORLD_BIRTH_CHOOSER_EVENT, handler);
  return () => {
    seatedChoosers -= 1;
    window.removeEventListener(OPEN_WORLD_BIRTH_CHOOSER_EVENT, handler);
  };
}

// -- The carriage: the chosen World, across the root navigation ---------------

/**
 * Remember that the next arrival in `worldKey` owes the person a governed
 * birth ceremony. sessionStorage because entering a World is a root
 * navigation on every shell (the browser shell reloads the document), and
 * per-tab because the gesture belongs to the tab it was made in.
 */
export function rememberPendingGovernedBirth(worldKey: string, now = Date.now()): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PENDING_BIRTH_STORAGE_KEY, JSON.stringify({ worldKey, at: now }));
  } catch {
    // Losing the carriage degrades to the ordinary room: the person opens
    // the ceremony with the + they can already see.
  }
}

/**
 * Consume the pending birth for exactly this World: true at most once, and
 * only while the gesture is fresh. A mismatched or stale record is dropped,
 * never re-aimed at whatever World happens to front next.
 */
export function consumePendingGovernedBirth(worldKey: string | null, now = Date.now()): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(PENDING_BIRTH_STORAGE_KEY);
    if (raw === null) return false;
    window.sessionStorage.removeItem(PENDING_BIRTH_STORAGE_KEY);
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return false;
    const record = parsed as { worldKey?: unknown; at?: unknown };
    if (typeof record.worldKey !== "string" || typeof record.at !== "number") return false;
    if (now - record.at > PENDING_BIRTH_TTL_MS) return false;
    return worldKey !== null && record.worldKey === worldKey;
  } catch {
    return false;
  }
}
